import React from "react";

import { Box, Button as BaseButton, CircularProgress, darken, lighten } from "@mui/material";

import stylizeComponent from "styles/stylizeComponent";

import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";

type ButtonColor = "black" | "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
type LoaderPolicy = "leftOfText" | "rightOfText" | "replaceText" | "startIcon" | "endIcon";
type ButtonSize = "xs" | "s" | "m" | "l";
type WrapPolicy = "noWrapEllipsis" | "noWrapScroll" | "wrap";

export interface ButtonProps extends Omit<MuiButtonProps, "color" | "size" | "variant" | "startIcon" | "endIcon"> {
  children: React.ReactNode;
  color?: ButtonColor;
  displayLoader?: boolean;
  endIcon?: React.ReactNode;
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>["rel"];
  loaderPolicy?: LoaderPolicy;
  onClick?: MuiButtonProps["onClick"];
  padding?: string | null;
  size?: ButtonSize;
  startIcon?: React.ReactNode;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  textColor?: string | null;
  variant?: "contained" | "outlined" | "text";
  wrapPolicy?: WrapPolicy;
}

const StyledButton = stylizeComponent(
  BaseButton,
  ["size2", "textColor"],
  ({ theme, ...props }: { theme: any; [key: string]: any }) => {
    let { padding } = props;
    if (!padding) {
      if (props.variant === "outlined") {
        switch (props.size2) {
          case "xs":
            padding = "3px 9px"; // Material small
            break;
          case "s":
            padding = "5px 15px"; // Material medium
            break;
          case "m":
            padding = "7px 21px"; // Material large
            break;
          case "l":
            padding = "11px 24px";
            break;
          default:
            padding = "5px 15px";
            break;
        }
      } else {
        switch (props.size2) {
          case "xs":
            padding = "4px 10px"; // Material small
            break;
          case "s":
            padding = "6px 16px"; // Material medium
            break;
          case "m":
            padding = "8px 22px"; // Material large
            break;
          case "l":
            padding = "12px 25px";
            break;
          default:
            padding = "6px 16px";
            break;
        }
      }
    }

    let textColorValue = null;
    if (props.textColor) {
      const listOfPaletteKeys = props.textColor.split(".");
      textColorValue = theme.palette;
      listOfPaletteKeys.forEach((key: string) => {
        textColorValue = textColorValue[key];
      });
    }

    let iconFontSize = null;
    switch (props.size2) {
      case "xs":
        iconFontSize = "12px"; // Material small
        break;
      case "s":
        iconFontSize = "14px"; // Material medium
        break;
      case "m":
        iconFontSize = "16px"; // Material large
        break;
      case "l":
        iconFontSize = "18px";
        break;
      default:
        iconFontSize = "14px";
        break;
    }

    let colorMain = null;
    let colorDisabled = null;
    let colorHover = null;
    switch (props.color) {
      case "inherit":
        colorMain = theme.palette.action.disabled;
        colorDisabled = theme.palette.action.disabled;
        colorHover = theme.palette.action.disabled;
        break;
      case "primary":
        colorMain = theme.palette.primary.main;
        colorDisabled = theme.palette.primary.disabled;
        colorHover = theme.palette.primary.hover;
        break;
      default:
        colorMain = theme.palette[props.color].main;
        colorDisabled = lighten(theme.palette[props.color].main, theme.palette.offsetColorVariantDisabled);
        colorHover = darken(theme.palette[props.color].main, theme.palette.offsetColorVariantHover);
        break;
    }

    return {
      "&.MuiButton-root": {
        borderRadius: "5px",
        fontSize: "14px",
        fontWeight: 500,
        padding,
        textAlign: "center",
        width: props.width || "100%",
        ".MuiCircularProgress-root": {
          width: iconFontSize,
          height: iconFontSize,
        },
        minWidth: props.minWidth || null,
      },
      "&.MuiButton-iconSizeSmall": {
        "& > *:first-of-type": {
          fontSize: iconFontSize,
        },
      },
      "&.MuiButton-contained": {
        "&.Mui-disabled": {
          color: theme.palette.getContrastText(colorMain), // In order to fake opacity
          backgroundColor: colorDisabled,
          ".MuiCircularProgress-root": {
            color: theme.palette.getContrastText(colorMain),
          },
        },
        "@media (hover: hover)": {
          "&:hover": {
            color: theme.palette.getContrastText(colorHover),
            backgroundColor: colorHover,
            ".MuiCircularProgress-root": {
              color: theme.palette.getContrastText(colorHover),
            },
          },
        },
      },
      "&.MuiButton-outlined": {
        backgroundColor: theme.palette.customized.buttonOutlinedBackground,
        "&.Mui-disabled": {
          border: "1px solid ".concat(
            theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
          ),
        },
      },
      "&.MuiButton-outlinedBlack": {
        "&.Mui-disabled": {
          opacity: 0.4,
        },
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: theme.palette.customized.buttonOutlinedBackground,
            border: `1px solid ${theme.palette.grey.grey2}`,
          },
          border: `1px solid ${theme.palette.grey.grey3}`,
        },
      },
      "&.MuiButton-outlinedPrimary": {
        "&.Mui-disabled": {
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main} 0.5`,
          opacity: 0.4,
          ".MuiCircularProgress-root": {
            color: theme.palette.primary.main,
          },
        },
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: theme.palette.customized.buttonOutlinedBackground,
          },
        },
      },
      "&.MuiButton-outlinedSecondary": {
        "&.Mui-disabled": {
          color: theme.palette.secondary.main,
          border: `1px solid ${theme.palette.secondary.main} 0.5`,
          opacity: 0.4,
          ".MuiCircularProgress-root": {
            color: theme.palette.secondary.main,
          },
        },
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: theme.palette.customized.buttonOutlinedBackground,
          },
        },
      },
      "&.MuiButton-text": {
        "&.Mui-disabled": {
          opacity: 0.4,
        },
      },
      "&.MuiButton-textPrimary": {
        "&.Mui-disabled": {
          color: theme.palette.primary.main,
          opacity: 0.4,
          ".MuiCircularProgress-root": {
            color: theme.palette.primary.main,
          },
        },
      },
      "&.MuiButton-textSecondary": {
        "&.Mui-disabled": {
          color: theme.palette.secondary.main,
          opacity: 0.4,
          ".MuiCircularProgress-root": {
            color: theme.palette.secondary.main,
          },
        },
      },
      "&.MuiButton-colorInherit": {
        color: textColorValue || "inherit",
        borderColor: "currentColor",
        "&.Mui-disabled": {
          color: "inherit",
          opacity: 0.4,
          ".MuiCircularProgress-root": {
            color: "inherit",
          },
        },
        ".MuiCircularProgress-root": {
          color: textColorValue || "inherit",
        },
      },
      "@media (hover: hover)": {
        "&:hover": {
          backgroundColor: null,
        },
      },
    };
  },
);

/**
 * A standard button with the appropriate style.
 */
const Button: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ButtonProps> & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      color = "primary",
      displayLoader = false,
      endIcon = null,
      loaderPolicy = "leftOfText",
      onClick = undefined,
      padding = null,
      size = "l",
      startIcon = null,
      textColor = null,
      variant = "contained",
      wrapPolicy = "wrap",
      ...props
    },
    ref,
  ) => {
    let className: string | null = null;
    if (wrapPolicy === "noWrapEllipsis") {
      className = "wz-g-overflow-hidden-text-ellipsis";
    } else if (wrapPolicy === "noWrapScroll") {
      className = "wz-g-overflow-scroll-hidden-bar";
    }

    const loader = <CircularProgress color="inherit" size={null} />;

    return (
      <StyledButton
        ref={ref}
        size="small"
        size2={size}
        onClick={onClick}
        color={color}
        variant={variant}
        disableRipple
        padding={padding}
        textColor={textColor}
        startIcon={loaderPolicy === "startIcon" && displayLoader ? loader : startIcon}
        endIcon={loaderPolicy === "endIcon" && displayLoader ? loader : endIcon}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
      >
        {loaderPolicy === "leftOfText" && displayLoader && (
          <>
            {loader}
            &nbsp;&nbsp;
          </>
        )}
        {loaderPolicy === "replaceText" && displayLoader && (
          <Box sx={{ lineHeight: "0", margin: "0rem 1rem" }}>
            {loader}
          </Box>
        )}
        {!(loaderPolicy === "replaceText" && displayLoader) && (
          <Box className={className || undefined}>
            {children}
          </Box>
        )}
        {loaderPolicy === "rightOfText" && displayLoader && (
          <>
            &nbsp;&nbsp;
            {loader}
          </>
        )}
      </StyledButton>
    );
  },
);

export default Button;
